/**
 * Keeps one bad render from blanking the whole application.
 *
 * Written after exactly that happened: the API was serving an older schema, a
 * cell read `undefined.join(...)`, and React unmounted the entire tree. The
 * user saw a white page with no clue what went wrong — the worst possible
 * presentation of a one-field mismatch.
 *
 * This is not a substitute for fixing the mismatch. It is a floor on how badly
 * one can fail: the shell survives, the error is named, and reloading is one
 * click away.
 */
import { Component, type ReactNode } from 'react'
import { Alert, Button, Code, Stack } from '@mantine/core'

interface Props {
  children: ReactNode
  /** Changing this resets the boundary — pass the route so navigating recovers. */
  resetKey?: string
}

interface State {
  error: Error | null
  lastKey?: string
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.error && props.resetKey !== state.lastKey) {
      return { error: null, lastKey: props.resetKey }
    }
    if (props.resetKey !== state.lastKey) return { lastKey: props.resetKey }
    return null
  }

  componentDidCatch(error: Error, info: unknown) {
    // Console, not a toast: this is for whoever is debugging, and a toast can
    // itself fail to render if the tree is already broken.
    console.error('render failed', error, info)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <Stack p="md">
        <Alert color="red" title="אירעה שגיאה בהצגת המסך">
          <Stack gap="sm" align="flex-start">
            <span>המסך נכשל בטעינה. נסה לרענן; אם הבעיה חוזרת, פנה למנהל המערכת.</span>
            <Code block>{this.state.error.message}</Code>
            <Button size="xs" onClick={() => window.location.reload()}>רענן</Button>
          </Stack>
        </Alert>
      </Stack>
    )
  }
}
